"use client";
import Link from "next/link";
import Image from "next/image";

import { ChevronRight, House } from "lucide-react";
import { formatDate } from "@/lib/helper";
import { BreadcrumbProps } from "@/types/ui";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

export default function Breadcrumb({
  title,
  titleHighlight,
  desc,
  breadcrumbTitle,
  meta_title,
  created_at,
}: BreadcrumbProps) {
  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = breadcrumbRef.current;
    const lines = headerRef.current;
    const description = descRef.current;

    if (!element) return;

    // Check if screen is lg (1024px) or larger
    const isLargeScreen = window.matchMedia("(min-width: 1024px)").matches;

    if (!isLargeScreen) {
      // For smaller screens, show elements immediately without animation
      if (element) gsap.set(element, { opacity: 1 });
      if (lines) gsap.set(lines, { opacity: 1 });
      if (description) gsap.set(description, { opacity: 1 });
      return;
    }

    // Animations only for lg screens and above
    gsap.set(element, {
      opacity: 0,
      y: -20,
      filter: "blur(8px)",
    });

    gsap.to(element, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      delay: 0.2,
      filter: "blur(0px)",
      ease: "power2.out",
      scrollTrigger: {
        trigger: element,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    gsap.set(lines, {
      opacity: 0,
      y: 40,
      filter: "blur(8px)",
    });

    gsap.to(lines, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.1,
      delay: 0.4,
      filter: "blur(0px)",
      ease: "power2.out",
      scrollTrigger: {
        trigger: element,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });

    gsap.set(description, {
      opacity: 0,
      y: 30,
      filter: "blur(8px)",
    });

    gsap.to(description, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay: 0.8,
      filter: "blur(0px)",
      ease: "power2.out",
      scrollTrigger: {
        trigger: element,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });
  }, []);

  return (
    <section
      className={`text-light text-center pb-12 pt-28 lg:py-20 relative overflow-hidden`}
    >
      {}
      <div className="absolute inset-0 z-0">
        <Image
          src="/assets/background-image.webp"
          alt="Background"
          fill
          className="object-cover"
          priority={true}
          fetchPriority="high"
          quality={85}
          sizes="100vw"
        />
      </div>
      {}
      <div className="absolute inset-0 bg-[#001640D4] z-1" />

      <div className="container relative z-10">
        {}
        {breadcrumbTitle && (
          <div
            ref={breadcrumbRef}
            className="flex items-center justify-center gap-2 flex-wrap text-sm font-medium mb-8 opacity-0"
          >
            <div className="flex items-center gap-2">
              <Link
                href={"/"}
                className="hover:text-[#f6842b] transition-colors flex items-center font-medium gap-2"
              >
                <House className="size-4 shrink-0" />
                Home
              </Link>

              <ChevronRight className="size-4 shrink-0" />
              <span className="text-white">{breadcrumbTitle}</span>
            </div>
          </div>
        )}

        {}
        <div ref={headerRef} className="opacity-0">
          {title && (
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-light mb-6 max-w-3xl 2xl:max-w-4xl mx-auto">
              {title || meta_title}{" "}
              {titleHighlight && (
                <span className="text-accent">{titleHighlight}</span>
              )}
            </h1>
          )}
          {created_at && (
            <span className="text-[20px] leading-[32px] font-bold">
              {formatDate(created_at || "")}
            </span>
          )}
        </div>

        {}
        {desc && (
          <div
            ref={descRef}
            className="max-w-lg md:max-w-2xl mx-auto opacity-0"
          >
            <p className="text-base sm:text-lg lg:text-xl text-white leading-relaxed font-medium">
              {desc}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
